import { CONFIG, Scene } from "~/route/globals";
import { showMarkerLabel } from "~/util/markerTitle";
import { ROUTE_CONST } from "~/route/const";
import { standardDistanceFormat, standardTimeFormat } from "~/util/format";
import { watch, watchAnimation } from "~/state/util";
import { store } from "~/state/store";
import { timeWithUserSpeed } from "~/util/user-speed";
import SceneLayerMarker from "~/scene/layer/marker";
import SceneMarkerBubble from "~/scene/marker/bubble";
import WasmStoreMarker from "~/wasm/store/wasm-store-marker";
import { WASM_ZINDEX } from "~/wasm/const";
import { generatePopupAngles } from "~/util/wasm";

export default class SummaryMarkers {
	_map: any;

	_layer: SceneLayerMarker;

	_lids: Array<string>;

	_ss: Array<string>;

	_markers: Array<SceneMarkerBubble>;

	_innerElements: Array<HTMLSpanElement>;

	_storeAnimationUnsubscribe: () => void;

	_showAlternatives: boolean;

	_showLabelInFrame: number;

	_resolveMarkerColision: boolean;

	_wasmMarkers: Array<WasmStoreMarker>;

	constructor(map) {
		this._map = map;
		this._layer = Scene.createLayerMarker("route-markers-summary");
		this._lids = [];
		this._ss = [];
		this._markers = [];
		this._wasmMarkers = [];
		this._innerElements = [];
		this._storeAnimationUnsubscribe = null;
		this._showAlternatives = true;
		this._showLabelInFrame = 0;
		this._resolveMarkerColision = true;
		this._setListeners();
	}

	destroy() {
		Mapy.CML.removeListeners(this._lids);
		Scene.removeListeners(this._ss);
		this._ss.length = 0;
		while (this._markers.length) {
			const marker = this._markers.pop();

			this._layer.removeMarker(marker);
			marker.destroy();
		}

		this._clearWasmMarkers();

		this._innerElements = [];
	}


	get layer() {
		return this._layer;
	}

	activate() {
		this._wasmMarkersVisibility(this._showAlternatives, Mapy.CML.state.route.index);
	}

	deactivate() {
		this._wasmMarkersVisibility(false);
	}

	set showAlternatives(show) {
		this._showAlternatives = show;
		const index = Mapy.CML.state.route.index;

		this._markers.forEach((marker, indx) => {
			if (indx !== index && marker) {
				this._layer[show ? "addMarker" : "removeMarker"](marker);
			}
		});

		if (this._markers[index]) {
			this._layer.addMarker(this._markers[index]);
		}

		this._wasmMarkersVisibility(show, index);
	}

	hoverMarker() {
		this._markerColision();
	}

	/**
	 * Nastaveni posluchacu.
	 */
	_setListeners() {
		this._lids.push(Mapy.CML.addListener(() => {
			while (this._markers.length) {
				const marker = this._markers.pop();

				this._layer.removeMarker(marker);
				marker.destroy();
			}

			this._clearWasmMarkers();
			this._innerElements = [];
		}, "route.responseData"));

		this._lids.push(Mapy.CML.addListener(index => {
			if (this._markers.length) {
				this._layer.addMarker(this._markers[index]);
				requestAnimationFrame(() => {
					this._markers.forEach((marker, indx) => {
						if (indx === index) {
							marker.toggleClassName("add", "active");
						} else {
							marker.toggleClassName("remove", "active");
						}
					});
				});

				for (let ind = 0, max = this._wasmMarkers.length; ind < max; ind++) {
					const wasmMarker = this._wasmMarkers[ind];

					wasmMarker.update({
						visual: ind === index ? "standard" : "inactive",
					});
				}
			}
		}, "route.index"));

		this._lids.push(Mapy.CML.addListener(index => {
			if (this._markers.length) {
				requestAnimationFrame(() => {
					this._markers.forEach((marker, indx) => {
						if (indx === index) {
							marker.toggleClassName("add", "hover");
						} else {
							marker.toggleClassName("remove", "hover");
						}
					});
				});
			}
		}, "route.hoverIndex"));

		this._lids.push(Mapy.CML.addListener((request, state) => {
			if (request) {
				while (this._markers.length) {
					const marker = this._markers.pop();

					this._layer.removeMarker(marker);
					marker.destroy();
				}

				this._innerElements = [];
				this._storeAnimationUnsubscribe?.();
				this._storeAnimationUnsubscribe = null;
			} else {
				const responseData = state.route.responseData;

				if (!responseData[state.route.index] || !responseData[state.route.index].geometry) {
					return;
				}

				responseData.forEach((rd, index) => {
					if (showMarkerLabel(state.route.showLabelInFrame, state.route.likeData?.usertitle, state.route.likeData?.title)) {
						this._showLabelInFrame = 1;
						this._buildMarkerLabel(Scene.coordsFromWGS84(rd.markerCoords[0], rd.markerCoords[1]), state.route.likeData.usertitle || state.route.likeData.title);
					} else {
						this._buildMarker(Scene.coordsFromWGS84(rd.markerCoords[0], rd.markerCoords[1]), rd.markerAzimuth, rd.totalLength, rd.totalTime, index, state.route);
					}
				});

				if (responseData[state.route.index] && responseData[state.route.index].geometry) {
					const wt = watch(store.getState, "map", watchAnimation);

					this._storeAnimationUnsubscribe = store.subscribe(wt(newMap => {
						if (newMap.animating) {
							this._changeZoom();
						}
					}));

					this._changeZoom();
				}
			}
		}, "route.request"));

		this._lids.push(Mapy.CML.addListener((trafficTotalTime, state) => {
			const routeState = state.route;
			const newMarkers: Array<WasmStoreMarker> = [];

			this._innerElements.forEach((element, index) => {
				const wasmMarker = this._wasmMarkers[index];
				// nechceme id
				const { id, ...curData } = wasmMarker.data;
				const length = standardDistanceFormat(routeState.responseData[index].totalLength).string;
				const time = standardTimeFormat(trafficTotalTime[index]).string;
				const timeIsBold = routeState.params.type === ROUTE_CONST.TYPE_CAR && ROUTE_CONST.TYPE_CAR_FAST_CRITERIONS.indexOf(routeState.params.criterion) !== -1;

				element.innerHTML = timeIsBold ? `<b>${time}</b>${length}` : `${time}<b>${length}</b>`;
				newMarkers.push(new WasmStoreMarker({
					...curData,
					customData: {
						...curData.customData,
						routePopupInfo: [{
							text: time,
							bold: timeIsBold,
						}, {
							text: length,
							bold: !timeIsBold,
						}],
					},
				}));
				wasmMarker.destroy();
			});

			this._wasmMarkers = newMarkers;
		}, "route.trafficTotalTime"));
	}

	_buildMarker(coords, azimuth, totalLength, totalTime, index, routeState) {
		const length = standardDistanceFormat(totalLength).string;
		const isTrip = routeState.items.length ? !!routeState.items[0].tripData[routeState.index] : false;
		const time = standardTimeFormat(timeWithUserSpeed(totalTime, isTrip ? routeState.params.tripCriterion : routeState.params.criterion)).string;
		const timeIsBold = routeState.params.type === ROUTE_CONST.TYPE_CAR && ROUTE_CONST.TYPE_CAR_FAST_CRITERIONS.indexOf(routeState.params.criterion) !== -1;
		const elm = Mapy.Util.node("span", {
			innerHTML: timeIsBold ? `<b>${time}</b>${length}` : `${time}<b>${length}</b>`,
		});
		const wasmMarker = new WasmStoreMarker({
			type: "route-popup-info",
			visual: index === routeState.index ? "standard" : "inactive",
			lon: coords.x,
			lat: coords.y,
			interactionType: "click-hover",
			customData: {
				popupArrowAngles: generatePopupAngles((azimuth === undefined ? 135 : Math.round(azimuth) + 180) % 360),
				routePopupInfo: [{
					text: time,
					bold: timeIsBold,
				}, {
					text: length,
					bold: !timeIsBold,
				}],
				order: index,
			},
			zIndex: WASM_ZINDEX.marker.routePopupInfo,
		});

		this._wasmMarkers.push(wasmMarker);

		if (routeState.responseData.length > 1) {
			elm.classList.add("alter");
		}

		if (azimuth !== undefined) {
			this._resolveMarkerColision = false;
		}

		const marker = Scene.createMarkerBubble(coords, null, {
			elm,
			className: `route-summary${index === routeState.index ? ` active` : ``}`,
			angle: azimuth === undefined ? 135 : Math.round(azimuth),
			arrowLength: 52,
			arrowCenter: [26, 26],
			bubbleHeight: 52,
		});

		elm.addEventListener("mouseenter", event => {
			if (event.target === elm && index !== routeState.index && index !== routeState.hoverIndex) {
				Mapy.CML.state = {
					route: {
						hoverIndex: index,
					},
				};
			}
		});
		elm.addEventListener("mouseleave", event => {
			if (event.target === elm && index !== routeState.index && index === routeState.hoverIndex) {
				Mapy.CML.state = {
					route: {
						hoverIndex: routeState.index,
					},
				};
			}
		});
		elm.addEventListener("mousemove", event => {
			event.stopPropagation();
		});
		elm.addEventListener("click", () => {
			if (!CONFIG.inFrame) {
				Mapy.CML.state = {
					route: {
						index,
					},
				};

				Mapy.getComponent("history").save();
			}
		});

		this._markers.push(marker);
		this._innerElements.push(elm);
	}

	_buildMarkerLabel(coords, label) {
		const marker = Scene.createMarkerMapyWindy(coords, null, { type: Scene.VISUAL.MARKER_WINDY.LITTLE });

		marker.setLabel(label);
		marker.setStyle("visibility", "hidden");
		this._markers.push(marker);
		this._layer.addMarker(marker);
	}

	_changeZoom() {
		const routeState = Mapy.CML.state.route;

		if (routeState.responseData.length && this._showAlternatives && this._showLabelInFrame) {
			const allPixels = [];

			routeState.responseData.forEach((rd, index) => {
				const coords = Scene.stringToCoords(rd.geometry);

				allPixels[index] = [];
				coords.forEach(item => {
					allPixels[index].push(Scene.coordsToPixel(item));
				});
			});

			if (this._resolveMarkerColision) {
				this._markers.forEach(marker => {
					marker.resolveColision(allPixels);
				});
			}

			this._markerColision(routeState);
		}
	}

	_markerColision(routeState = Mapy.CML.state.route) {
		if (this._markers.length > 1) {
			const hoverIndex = routeState.hoverIndex;
			const indexes = [hoverIndex];
			const showIndexes = [hoverIndex];

			this._markers.forEach((marker, index) => {
				this._layer.removeMarker(marker);

				if (index !== hoverIndex) {
					indexes.push(index);
					showIndexes.push(index);
				}
			});

			for (let indx = 0; indx < indexes.length && indexes.length > 1; indx++) {
				const marker = this._markers[indx];

				indexes.forEach(index => {
					if (showIndexes.indexOf(index) > -1 && showIndexes.indexOf(indx) > -1 && index !== hoverIndex && index !== indx && marker?.intersectOtherMarker && marker.intersectOtherMarker(this._markers[index])) {
						showIndexes.splice(showIndexes.indexOf(index), 1);
					}
				});
			}

			if (this._showAlternatives) {
				showIndexes.forEach(index => {
					const marker = this._markers[index];

					marker && this._layer.addMarker(marker);
				});
			} else {
				this._layer.addMarker(this._markers[routeState.index]);
			}
		} else if (this._markers.length === 1) {
			this._layer.addMarker(this._markers[routeState.index]);
		}
	}

	_clearWasmMarkers() {
		for (const wasmMarker of this._wasmMarkers) {
			wasmMarker.destroy();
		}

		this._wasmMarkers.length = 0;
	}

	_wasmMarkersVisibility(visible: boolean, index = -1) {
		for (let ind = 0, max = this._wasmMarkers.length; ind < max; ind++) {
			const wasmMarker = this._wasmMarkers[ind];

			wasmMarker.update({
				visible: visible || ind === index,
			});
		}
	}
}
